import { useMutation, useSubscription } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { FormLabel, Grid, LinearProgress, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { ExecuteProjectImportResultChange } from 'api/graphQL/subscriptions/import.subscription'
import { ProductFamilyPublishResultChangeSubscription } from 'api/graphQL/subscriptions/publish.subscriptions'
import { ExecuteImportData } from 'api/graphQL/types/project.types'
import { PublishResult } from 'bpm/models/contentManagement'
import clsx from 'clsx'
import { ImportUploadFileInfo } from 'models/projectImport'
import { onEscKeyPressOnDialog } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import { FilesToPublish } from '../..'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
            paddingBottom: theme.spacing(0.25),
        },
    },
    showMore: {
        cursor: 'pointer',
        marginTop: theme.spacing(0.5),
        textAlign: 'end',
        paddingRight: theme.spacing(2),
    },
    confirmationText: {
        paddingTop: theme.spacing(2),
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
    },
    successLabel: {
        color: '#33B55B',
        textAlign: 'center',
        marginTop: theme.spacing(0.5),
    },
    dialogCont: {
        '&.MuiDialogContent-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
        fontSize: pxToRem(18),
    },
    actionButton: {
        color: '#A78966',
        fontSize: pxToRem(18),
        width: 108,
        marginBottom: theme.spacing(2),
    },
    progressLabel: {
        fontSize: pxToRem(14),
    },
    warningDialogCont: {
        '& .MuiDialog-paperScrollPaper': {
            width: pxToRem(540),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    title: {
        fontSize: pxToRem(14),
        paddingBottom: theme.spacing(0.5),
        color: 'rgba(0, 0, 0, 0.54)',
        whiteSpace: 'pre-line',
    },

    gridItem: {
        overflowY: 'auto',
        maxHeight: 400,
    },
    gridItemCollapsed: {
        overflowY: 'hidden',
        maxHeight: 400,
    },
    progressBar: {
        background: '#9AC4E8',
        border: '1px solid #9AC4E8',
        boxSizing: 'border-box',
        borderRadius: '100px',
        height: 8,
        width: '100%',
        '& .MuiLinearProgress-bar1Indeterminate': {
            background: '#006FC6',
            borderRadius: '100px',
        },
        '& .MuiLinearProgress-bar2Indeterminate': {
            background: '#006FC6',
            borderRadius: '100px',
        },
    },
    labelText: {
        fontSize: pxToRem(12),
        paddingTop: theme.spacing(0.5),
        paddingLeft: theme.spacing(0.5),
    },
    labelTextSuccess: {
        fontSize: pxToRem(12),
        paddingTop: theme.spacing(0.5),
        paddingLeft: theme.spacing(0.5),
        color: '#33B55B',
    },
    progressBarContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        justifyContent: 'space-between',
    },
}))

export interface ImportExecutionProgressProps {
    instanceId?: string
    handleDialogClose: VoidFunction
    handleChangeFileStatus: (
        fileData?: Partial<ExecuteImportData>,
        publishData?: PublishResult
    ) => void
    title: string[]
    isImportCompleted?: boolean
    allImportedFiles?: Partial<ImportUploadFileInfo>[]
    showAllFiles?: boolean
    handleImportingFilesView?: (value: boolean) => void
    isBPImport: boolean
    publishInstanceId?: string
    importedFilesToBePublished?: boolean
    filesToPublish?: FilesToPublish[]
}

const ImportExecutionProgress: React.FC<ImportExecutionProgressProps> = ({
    instanceId,
    handleDialogClose,
    handleChangeFileStatus,
    title,
    isImportCompleted,
    showAllFiles,
    handleImportingFilesView,
    isBPImport,
    publishInstanceId,
    importedFilesToBePublished,
    filesToPublish,
}) => {
    const classes = useStyles()
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const handleClickMoreFiles = React.useCallback(
        (value: boolean) => {
            if (handleImportingFilesView) {
                handleImportingFilesView?.(value)
            }
        },
        [handleImportingFilesView]
    )
    const {
        data: executeProjectImportData,
        error: executeProjectImportError,
    } = useSubscription<{
        onExecuteProjectImportResultChange: Partial<ExecuteImportData> | null
    }>(ExecuteProjectImportResultChange, {
        variables: { instanceId },
        skip: !instanceId || instanceId === '',
    })
    const {
        data: productFamilyPublishData,
        error: productFamilyPublishError,
    } = useSubscription<{
        onProductFamilyPublishResultChange: PublishResult | null
    }>(ProductFamilyPublishResultChangeSubscription, {
        variables: { instanceId: publishInstanceId },
        skip: !publishInstanceId || publishInstanceId === '',
    })
    const handleCancel = React.useCallback(() => {
        if (!isImportCompleted) return
        if (!!instanceId) {
            handleDialogClose?.()
            handleClickMoreFiles(false)
        }
    }, [instanceId, handleImportingFilesView, handleDialogClose])
    React.useEffect(() => {
        if (!!showGlobalSnackbar && !!executeProjectImportError) {
            showGlobalSnackbar({
                variables: {
                    message: executeProjectImportError.message,
                    variant: 'error',
                    id: `PublishPage_printStart`,
                },
            })
        }
    }, [executeProjectImportError, showGlobalSnackbar])

    React.useEffect(() => {
        if (
            !!executeProjectImportData &&
            !!executeProjectImportData.onExecuteProjectImportResultChange
        ) {
            const statusData =
                executeProjectImportData.onExecuteProjectImportResultChange
            handleChangeFileStatus?.(statusData, undefined)
            handleClickMoreFiles(false)
        }
    }, [executeProjectImportData, handleChangeFileStatus])
    React.useEffect(() => {
        if (
            !!productFamilyPublishData &&
            !!productFamilyPublishData.onProductFamilyPublishResultChange
        ) {
            const areAllUploadedFilesPublished =
                filesToPublish?.length !== 0 &&
                filesToPublish?.length ===
                    productFamilyPublishData?.onProductFamilyPublishResultChange
                        ?.productFamilyPublishResults?.length
            if (
                productFamilyPublishData?.onProductFamilyPublishResultChange &&
                areAllUploadedFilesPublished
            ) {
                handleChangeFileStatus?.(
                    undefined,
                    productFamilyPublishData?.onProductFamilyPublishResultChange
                )
            }
        }
        if (productFamilyPublishError) {
            handleCancel()
        }
    }, [
        productFamilyPublishData,
        productFamilyPublishError,
        handleChangeFileStatus,
        filesToPublish,
    ])
    return (
        <Dialog
            open={instanceId !== '' || publishInstanceId !== ''}
            data-testid="executionProgressDialog"
            className={classes.warningDialogCont}
            aria-labelledby="publishProgress"
            onClose={onEscKeyPressOnDialog(handleCancel!)}
        >
            <DialogTitle className={classes.dialogTitle}>
                Import {importedFilesToBePublished ? '+ Publish' : ''} Progress
            </DialogTitle>
            <DialogContent className={classes.dialogCont}>
                <GridContainer direction="column">
                    <GridItem
                        className={clsx(
                            title?.length < 8 && classes.gridItemCollapsed,
                            title?.length >= 8 &&
                                showAllFiles &&
                                classes.gridItem
                        )}
                    >
                        <FormLabel className={classes.title}>
                            {(title?.length < 8 || showAllFiles
                                ? title
                                : title?.slice(
                                      0,

                                      7
                                  )
                            )?.map((item) => {
                                return `${item}\n`
                            })}
                        </FormLabel>
                    </GridItem>

                    {title?.length < 8 ? (
                        <></>
                    ) : showAllFiles ? (
                        <Typography
                            variant="body2"
                            className={classes.showMore}
                            onClick={() => handleClickMoreFiles(false)}
                        >
                            Show Less...
                        </Typography>
                    ) : (
                        <Typography
                            variant="body2"
                            className={classes.showMore}
                            onClick={() => handleClickMoreFiles(true)}
                        >
                            See More...
                        </Typography>
                    )}
                    {isImportCompleted ? (
                        <div
                            data-testid="importExecutionSuccessText"
                            className={classes.successLabel}
                        >
                            Success!
                        </div>
                    ) : (
                        <>
                            <Grid className={classes.progressBarContainer}>
                                {isImportCompleted ? (
                                    <LinearProgress
                                        variant="determinate"
                                        value={100}
                                        data-testid="importExecutionSuccess"
                                        className={classes.progressBar}
                                    />
                                ) : (
                                    <LinearProgress
                                        data-testid="importExecutionProgress"
                                        className={classes.progressBar}
                                    />
                                )}
                            </Grid>
                            <span className={classes.progressLabel}>
                                <Typography
                                    className={classes.confirmationText}
                                >
                                    Specpoint is importing{' '}
                                    {importedFilesToBePublished
                                        ? 'and publishing'
                                        : ''}{' '}
                                    your content into the
                                    {isBPImport
                                        ? ' element library'
                                        : ' project'}
                                    . You will be notified when the process is
                                    complete.
                                </Typography>
                            </span>
                        </>
                    )}
                </GridContainer>
            </DialogContent>
            <DialogActions className={classes.dialogAction}>
                <Button
                    disabled={!isImportCompleted}
                    data-testid="cancelButton"
                    className={classes.actionButton}
                    onClick={handleCancel}
                >
                    {isImportCompleted ? 'Close' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
export default ImportExecutionProgress
